import { useCallback, useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Bot, ChevronDown, ChevronRight, Download, ExternalLink, Loader2, RefreshCw, Search, UserRound } from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { appInboxAudit, appInboxAuditCsv } from "@/lib/api";

const VIA_TONE = { autopilot: "bg-violet-50 text-violet-800 border-violet-300", approved: "bg-sky-50 text-sky-800 border-sky-300", manual: "bg-slate-50 text-slate-700 border-slate-300" };
const fmtWhen = (iso) => (iso ? new Date(iso).toLocaleString("en-GB", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }) : "");

function Detail({ r }) {
  const e = r.email || {}, d = r.deal || {}, dec = r.decision || {}, f = r.files || {}, o = r.outcomes || {};
  const Row = ({ k, v }) => ((Array.isArray(v) ? v.length > 0 : v || v === 0) ? <div className="flex gap-2"><span className="w-36 shrink-0 text-slate-500">{k}</span><span className="min-w-0 break-words">{Array.isArray(v) ? v.join(" · ") : String(v)}</span></div> : null);
  return (
    <div className="grid gap-x-6 gap-y-0.5 border-t bg-slate-50/70 px-3 py-2 text-[11px] sm:grid-cols-2" data-testid={`audit-detail-${r.audit_id}`}>
      <div className="space-y-0.5">
        <Row k="Email from" v={e.from} /><Row k="Subject" v={e.subject} /><Row k="Received" v={fmtWhen(e.received)} />
        <Row k="Read as" v={dec.intent ? `${dec.intent}${dec.confidence != null ? ` · ${dec.confidence}%` : ""}` : ""} />
        <Row k="Proposed" v={dec.detail} />
        <Row k="AI suggested" v={dec.suggested_status} />
        <Row k="Status" v={dec.status_set ? `${d.status_before || "?"} → ${dec.status_set}` : d.status_before ? `kept at ${d.status_before}` : ""} />
        <Row k="Why" v={dec.status_reason} />
      </div>
      <div className="space-y-0.5">
        <Row k="Files filed" v={f.uploaded?.length ? f.uploaded : f.queued ? `${f.queued} queued` : ""} />
        <Row k="Duplicates skipped" v={f.skipped} /><Row k="Files failed" v={f.failed} />
        <Row k="Email → Done" v={o.moved_to_done ? "yes" : ""} /><Row k="Acknowledgement" v={o.ack_sent_to} /><Row k="Condition alert" v={o.alert_sent_to} />
        <Row k="Forwarded to" v={o.forwarded_to} /><Row k="Warnings" v={r.warnings} /><Row k="Error" v={r.error} />
        <Row k="Finished" v={fmtWhen(r.finished_at)} />
      </div>
    </div>
  );
}

function AuditRow({ r }) {
  const [open, setOpen] = useState(false);
  const d = r.deal || {};
  return (
    <div className="border-b last:border-b-0" data-testid={`audit-row-${r.audit_id}`}>
      <button type="button" className="flex w-full items-start gap-2 px-3 py-2 text-left hover:bg-slate-50" onClick={() => setOpen((o) => !o)} data-testid={`audit-toggle-${r.audit_id}`}>
        {open ? <ChevronDown className="mt-0.5 h-3 w-3 shrink-0 text-slate-400" /> : <ChevronRight className="mt-0.5 h-3 w-3 shrink-0 text-slate-400" />}
        <Badge variant="outline" className={cn("shrink-0 text-[9px]", VIA_TONE[r.via] || VIA_TONE.manual)} data-testid={`audit-via-${r.audit_id}`}>
          {r.via === "autopilot" ? <><Bot className="mr-0.5 h-2.5 w-2.5" />AP</> : <><UserRound className="mr-0.5 h-2.5 w-2.5" />{r.actor}</>}
        </Badge>
        <span className="min-w-0 flex-1 text-[11px] leading-snug text-slate-800" data-testid={`audit-readable-${r.audit_id}`}>{r.readable}</span>
        {(r.error || r.files?.failed?.length > 0) && <Badge variant="outline" className="shrink-0 border-red-300 bg-red-50 text-[9px] text-red-700">problem</Badge>}
        {d.item_url && <a href={d.item_url} target="_blank" rel="noreferrer" className="shrink-0 text-blue-600" onClick={(e) => e.stopPropagation()} title="Open the deal in Monday" data-testid={`audit-monday-${r.audit_id}`}><ExternalLink className="h-3 w-3" /></a>}
      </button>
      {open && <Detail r={r} />}
    </div>
  );
}

// Applications-inbox audit log — the readable record of what the portal / Autopilot did to each email (Dean, 22 Oct 2026).
export function InboxAuditLog() {
  const [f, setF] = useState({ sf: "", q: "", who: "all", action: "", days: 14 });
  const [state, setState] = useState({ loading: true, items: [], counts: {}, actions: {} });
  const [csvBusy, setCsvBusy] = useState(false);
  const params = useCallback(() => ({ sf: f.sf, q: f.q, action: f.action, days: f.days, only_auto: f.who === "autopilot", only_people: f.who === "people", limit: 400 }), [f]);
  const load = useCallback(async () => {
    setState((s) => ({ ...s, loading: true }));
    try {
      const r = await appInboxAudit(params());
      setState({ loading: false, items: r.items || [], counts: r.counts || {}, actions: r.actions || {} });
    } catch (e) { setState((s) => ({ ...s, loading: false })); toast.error(e.response?.data?.detail || e.message); }
  }, [params]);
  useEffect(() => { load(); }, [load]);
  const exportCsv = async () => {
    setCsvBusy(true);
    try {
      const blob = await appInboxAuditCsv(params());
      const url = URL.createObjectURL(blob); const a = document.createElement("a"); a.href = url; a.download = `applications-inbox-audit-${new Date().toISOString().slice(0, 10)}.csv`; a.click(); URL.revokeObjectURL(url);
    } catch (e) { toast.error(e.response?.data?.detail || e.message); } finally { setCsvBusy(false); }
  };
  const c = state.counts;
  return (
    <div data-testid="inbox-audit-log">
      <div className="flex flex-wrap items-center gap-2 border-b bg-slate-50 px-3 py-2">
        <div className="relative">
          <Search className="pointer-events-none absolute left-2 top-1.5 h-3 w-3 text-slate-400" />
          <Input value={f.sf} onChange={(e) => setF({ ...f, sf: e.target.value })} placeholder="SF number" className="h-7 w-32 pl-6 text-xs" data-testid="audit-filter-sf" />
        </div>
        <Input value={f.q} onChange={(e) => setF({ ...f, q: e.target.value })} placeholder="Search sender, subject, deal, warning…" className="h-7 w-64 text-xs" data-testid="audit-filter-q" />
        <select value={f.who} onChange={(e) => setF({ ...f, who: e.target.value })} className="h-7 rounded border bg-white px-2 text-xs" data-testid="audit-filter-who">
          <option value="all">Everyone</option><option value="autopilot">Autopilot only (AP)</option><option value="people">People only</option>
        </select>
        <select value={f.action} onChange={(e) => setF({ ...f, action: e.target.value })} className="h-7 rounded border bg-white px-2 text-xs" data-testid="audit-filter-action">
          <option value="">All actions</option>
          {Object.entries(state.actions).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select>
        <select value={f.days} onChange={(e) => setF({ ...f, days: Number(e.target.value) })} className="h-7 rounded border bg-white px-2 text-xs" data-testid="audit-filter-days">
          {[1, 3, 7, 14, 30, 90].map((d) => <option key={d} value={d}>Last {d} day{d === 1 ? "" : "s"}</option>)}
        </select>
        <span className="ml-auto text-[11px] text-slate-500" data-testid="audit-counts">
          {state.items.length} rows · AP {c.autopilot || 0} · approved {c.approved || 0} · manual {c.manual || 0}
        </span>
        <Button size="sm" variant="outline" className="h-7 px-2 text-xs" onClick={load} disabled={state.loading} data-testid="audit-refresh">{state.loading ? <Loader2 className="h-3 w-3 animate-spin" /> : <RefreshCw className="h-3 w-3" />}</Button>
        <Button size="sm" variant="outline" className="h-7 px-2 text-xs" onClick={exportCsv} disabled={csvBusy || !state.items.length} data-testid="audit-export-csv"><Download className="mr-1 h-3 w-3" />CSV</Button>
      </div>
      <p className="border-b px-3 py-1.5 text-[10.5px] text-slate-500" data-testid="audit-legend">
        <b>AP</b> = Autopilot did it on its own (its Monday posts start with “AP:”) · <b>name</b> = a person posted it or approved the draft. Click a row for the full record; CSV exports what's filtered.
      </p>
      {state.loading && !state.items.length ? <div className="p-6 text-center text-xs text-slate-500"><Loader2 className="mx-auto h-4 w-4 animate-spin" /></div>
        : !state.items.length ? <div className="p-6 text-center text-xs text-slate-500" data-testid="audit-empty">Nothing in the audit log for this filter yet — rows appear from the next thing the inbox does.</div>
        : <div>{state.items.map((r) => <AuditRow key={r.audit_id || r.at + r.action} r={r} />)}</div>}
    </div>
  );
}
